import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Shield, 
  Bell, 
  MapPin, 
  Car, 
  Radio, 
  Video, 
  Volume2,
  Activity,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Info,
  Monitor,
  X,
  Check
} from 'lucide-react';

const SYSMON_ALL_APPS = [
  'alarms',
  'lta-wifi',
  'geolocation',
  'safety'
];

// Configuration categories with their available settings
const CONFIG_CATEGORIES = {
  safety: {
    id: 'safety',
    name: 'Safety',
    icon: Shield,
    color: 'red',
    groups: [
      {
        name: 'Speed Limit',
        description: 'Configure overspeed alerts and limits',
        settings: [
          { path: 'speedLimit.defaultLimit', type: 'int', label: 'Default Limit', unit: 'km/h', default: 150, min: 0, max: 300 },
          { path: 'speedLimit.buzzer', type: 'bool', label: 'Buzzer Alert', default: false },
        ]
      },
      {
        name: 'Harsh Acceleration & Braking',
        description: 'Configure harsh driving detection thresholds',
        settings: [
          { path: 'harshAccelBraking.accelerationThreshold', type: 'int', label: 'Acceleration Threshold', unit: 'g', default: 0, min: 0, max: 100 },
          { path: 'harshAccelBraking.brakingThreshold', type: 'int', label: 'Braking Threshold', unit: 'g (negative)', default: 0, min: -100, max: 0 },
          { path: 'harshAccelBraking.gpsSpdComparisonFactor', type: 'float', label: 'GPS Speed Factor', default: 1.0, min: 0, max: 5, step: 0.1 },
          { path: 'harshAccelBraking.buzzer', type: 'bool', label: 'Buzzer Alert', default: false },
        ]
      }
    ],
    settings: [
      { path: 'speedLimit.defaultLimit', type: 'int', label: 'Default speed limit (km/h)', default: 150 },
      { path: 'speedLimit.buzzer', type: 'bool', label: 'Enable buzzer for overspeed', default: false },
      { path: 'harshAccelBraking.accelerationThreshold', type: 'int', label: 'Harsh acceleration threshold', default: 0 },
      { path: 'harshAccelBraking.brakingThreshold', type: 'int', label: 'Harsh braking threshold (negative)', default: 0 },
      { path: 'harshAccelBraking.gpsSpdComparisonFactor', type: 'float', label: 'GPS speed comparison factor', default: 1.0 },
      { path: 'harshAccelBraking.buzzer', type: 'bool', label: 'Enable buzzer for accel/braking', default: false },
    ]
  },
  alarms: {
    id: 'alarms',
    name: 'Alarms',
    icon: Bell,
    color: 'orange',
    groups: [
      {
        name: 'Headway Warning',
        description: 'Configure following distance alerts',
        settings: [
          { path: 'headway.minDistance', type: 'int', label: 'Min Distance', unit: 'meters', default: 0, min: 0, max: 500 },
          { path: 'headway.threshold', type: 'int', label: 'Threshold', default: 0, min: 0, max: 100 },
          { path: 'headway.buzzer', type: 'bool', label: 'Buzzer Alert', default: false },
        ]
      },
      {
        name: 'Forward Collision Warning (FCW)',
        description: 'Configure collision warning alerts',
        settings: [
          { path: 'fcw.buzzer', type: 'bool', label: 'Buzzer Alert', default: false },
        ]
      },
      {
        name: 'Forward Collision Mitigation (FCM)',
        description: 'Configure collision mitigation system',
        settings: [
          { path: 'fcm.minSpeed', type: 'float', label: 'Min Speed', unit: 'km/h', default: 0, min: 0, max: 200, step: 0.1 },
          { path: 'fcm.timeWindow', type: 'int', label: 'Time Window', unit: 'sec', default: 0, min: 0, max: 60 },
          { path: 'fcm.buzzer', type: 'bool', label: 'Buzzer Alert', default: false },
        ]
      },
      {
        name: 'Dynamic Stability Control (DSC)',
        description: 'Configure vehicle stability alerts',
        settings: [
          { path: 'dsc.minSpeed', type: 'float', label: 'Min Speed', unit: 'km/h', default: 0, min: 0, max: 200, step: 0.1 },
          { path: 'dsc.timeWindow', type: 'int', label: 'Time Window', unit: 'sec', default: 0, min: 0, max: 60 },
          { path: 'dsc.buzzer', type: 'bool', label: 'Buzzer Alert', default: false },
        ]
      }
    ],
    settings: [
      { path: 'headway.minDistance', type: 'int', label: 'Minimum headway distance (meters)', default: 0 },
      { path: 'headway.threshold', type: 'int', label: 'Headway threshold', default: 0 },
      { path: 'headway.buzzer', type: 'bool', label: 'Enable buzzer for headway', default: false },
      { path: 'fcw.buzzer', type: 'bool', label: 'Buzzer Forward Collision Warning', default: false },
      { path: 'fcm.minSpeed', type: 'float', label: 'Minimum speed Collision Mitigation', default: 0 },
      { path: 'fcm.timeWindow', type: 'int', label: 'Time window Collision Mitigation', default: 0 },
      { path: 'fcm.buzzer', type: 'bool', label: 'Buzzer Collision Mitigation', default: false },
      { path: 'dsc.minSpeed', type: 'float', label: 'Minimum speed Stability Control', default: 0 },
      { path: 'dsc.timeWindow', type: 'int', label: 'Time window Stability Control', default: 0 },
      { path: 'dsc.buzzer', type: 'bool', label: 'Buzzer Dynamic Stability Control', default: false },
    ]
  },
  geolocation: {
    id: 'geolocation',
    name: 'Geolocation',
    icon: MapPin,
    color: 'blue',
    groups: [
      {
        name: 'Position Reporting (Ignition ON)',
        description: 'Configure position updates when vehicle is running',
        settings: [
          { path: 'position.simplePosPeriodIgnOn', type: 'int', label: 'Simple Position Period', unit: 'sec', default: 0, min: 0, max: 3600 },
          { path: 'position.completePosPeriodIgnOn', type: 'int', label: 'Complete Position Period', unit: 'sec', default: 0, min: 0, max: 3600 },
        ]
      },
      {
        name: 'Position Reporting (Ignition OFF)',
        description: 'Configure position updates when vehicle is parked',
        settings: [
          { path: 'position.simplePosPeriodIgnOff', type: 'int', label: 'Simple Position Period', unit: 'sec', default: 0, min: 0, max: 3600 },
          { path: 'position.completePosPeriodIgnOff', type: 'int', label: 'Complete Position Period', unit: 'sec', default: 0, min: 0, max: 3600 },
        ]
      },
      {
        name: 'Angle Trigger',
        description: 'Configure heading change trigger',
        settings: [
          { path: 'position.simpleAngle', type: 'int', label: 'Angle Threshold', unit: 'degrees', default: 0, min: 0, max: 180 },
        ]
      }
    ],
    settings: [
      { path: 'position.simplePosPeriodIgnOn', type: 'int', label: 'Simple position period (ignition ON)', default: 0 },
      { path: 'position.completePosPeriodIgnOn', type: 'int', label: 'Complete position period (ignition ON)', default: 0 },
      { path: 'position.simplePosPeriodIgnOff', type: 'int', label: 'Simple position period (ignition OFF)', default: 0 },
      { path: 'position.completePosPeriodIgnOff', type: 'int', label: 'Complete position period (ignition OFF)', default: 0 },
      { path: 'position.simpleAngle', type: 'int', label: 'Angle for simple position trigger', default: 0 },
    ]
  },
  'vehicle-data': {
    id: 'vehicle-data',
    name: 'Vehicle Data',
    icon: Car,
    color: 'green',
    groups: [
      {
        name: 'Speed Source',
        description: 'Configure primary speed data source',
        settings: [
          { path: 'speed.highPriority', type: 'str', label: 'Priority Source', default: 'gps', options: ['canJ1939WheelSpeed', 'canJ1939Tachograph', 'gps'] },
        ]
      },
      {
        name: 'Odometer',
        description: 'Configure odometer data source',
        settings: [
          { path: 'odometer.sourceAddressInUse', type: 'int', label: 'CAN Address', default: -1, min: -1, max: 255 },
        ]
      }
    ],
    settings: [
      { path: 'speed.highPriority', type: 'str', label: 'Priority speed source', default: 'gps', options: ['canJ1939WheelSpeed', 'canJ1939Tachograph', 'gps'] },
      { path: 'odometer.sourceAddressInUse', type: 'int', label: 'CAN address for odometer', default: -1 },
    ]
  },
  'vehicle-data-dispatcher': {
    id: 'vehicle-data-dispatcher',
    name: 'Vehicle Data Dispatcher',
    icon: Radio,
    color: 'purple',
    groups: [
      {
        name: 'General',
        description: 'Enable/disable event publishing',
        settings: [
          { path: 'onEventPublishEnabled', type: 'bool', label: 'Auto Event Publishing', default: false },
        ]
      },
      {
        name: 'Speed Limit Events',
        description: 'Time window for speeding events',
        settings: [
          { path: 'safetySpeedLimit.priorTimeSec', type: 'int', label: 'Pre-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
          { path: 'safetySpeedLimit.postTimeSec', type: 'int', label: 'Post-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
        ]
      },
      {
        name: 'Harsh Accel/Braking Events',
        description: 'Time window for harsh driving events',
        settings: [
          { path: 'safetyHarshAccelBraking.priorTimeSec', type: 'int', label: 'Pre-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
          { path: 'safetyHarshAccelBraking.postTimeSec', type: 'int', label: 'Post-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
        ]
      },
      {
        name: 'Collision Mitigation Events',
        description: 'Time window for FCM events',
        settings: [
          { path: 'alarmsCollisionMitigation.priorTimeSec', type: 'int', label: 'Pre-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
          { path: 'alarmsCollisionMitigation.postTimeSec', type: 'int', label: 'Post-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
        ]
      },
      {
        name: 'Collision Warning Events',
        description: 'Time window for FCW events',
        settings: [
          { path: 'alarmsCollisionWarning.priorTimeSec', type: 'int', label: 'Pre-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
          { path: 'alarmsCollisionWarning.postTimeSec', type: 'int', label: 'Post-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
        ]
      },
      {
        name: 'Dynamic Stability Events',
        description: 'Time window for DSC events',
        settings: [
          { path: 'alarmsDynamicStability.priorTimeSec', type: 'int', label: 'Pre-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
          { path: 'alarmsDynamicStability.postTimeSec', type: 'int', label: 'Post-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
        ]
      },
      {
        name: 'Haptic Warning Events',
        description: 'Time window for haptic events',
        settings: [
          { path: 'alarmsHapticWarning.priorTimeSec', type: 'int', label: 'Pre-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
          { path: 'alarmsHapticWarning.postTimeSec', type: 'int', label: 'Post-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
        ]
      },
      {
        name: 'Headway Warning Events',
        description: 'Time window for headway events',
        settings: [
          { path: 'alarmsHeadwayWarning.priorTimeSec', type: 'int', label: 'Pre-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
          { path: 'alarmsHeadwayWarning.postTimeSec', type: 'int', label: 'Post-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
        ]
      },
      {
        name: 'Telemetry Data Request',
        description: 'Time window for telemetry events',
        settings: [
          { path: 'telemetryDataRequest.priorTimeSec', type: 'int', label: 'Pre-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
          { path: 'telemetryDataRequest.postTimeSec', type: 'int', label: 'Post-event Time', unit: 'sec', default: 0, min: 0, max: 300 },
        ]
      }
    ],
    settings: [
      { path: 'onEventPublishEnabled', type: 'bool', label: 'Enable automatic event publishing', default: false },
      { path: 'safetySpeedLimit.priorTimeSec', type: 'int', label: 'Pre-event time speeding', default: 0 },
      { path: 'safetySpeedLimit.postTimeSec', type: 'int', label: 'Post-event time speeding', default: 0 },
      { path: 'safetyHarshAccelBraking.priorTimeSec', type: 'int', label: 'Pre-event time harsh accel/braking', default: 0 },
      { path: 'safetyHarshAccelBraking.postTimeSec', type: 'int', label: 'Post-event time harsh accel/braking', default: 0 },
      { path: 'alarmsCollisionMitigation.priorTimeSec', type: 'int', label: 'Pre-event time collision mitigation', default: 0 },
      { path: 'alarmsCollisionMitigation.postTimeSec', type: 'int', label: 'Post-event time collision mitigation', default: 0 },
      { path: 'alarmsCollisionWarning.priorTimeSec', type: 'int', label: 'Pre-event time collision warning', default: 0 },
      { path: 'alarmsCollisionWarning.postTimeSec', type: 'int', label: 'Post-event time collision warning', default: 0 },
      { path: 'alarmsDynamicStability.priorTimeSec', type: 'int', label: 'Pre-event time stability', default: 0 },
      { path: 'alarmsDynamicStability.postTimeSec', type: 'int', label: 'Post-event time stability', default: 0 },
      { path: 'alarmsHapticWarning.priorTimeSec', type: 'int', label: 'Pre-event time haptic', default: 0 },
      { path: 'alarmsHapticWarning.postTimeSec', type: 'int', label: 'Post-event time haptic', default: 0 },
      { path: 'alarmsHeadwayWarning.priorTimeSec', type: 'int', label: 'Pre-event time headway', default: 0 },
      { path: 'alarmsHeadwayWarning.postTimeSec', type: 'int', label: 'Post-event time headway', default: 0 },
      { path: 'telemetryDataRequest.priorTimeSec', type: 'int', label: 'Pre-event time telemetry', default: 0 },
      { path: 'telemetryDataRequest.postTimeSec', type: 'int', label: 'Post-event time telemetry', default: 0 },
    ]
  },
  'video-events': {
    id: 'video-events',
    name: 'Video Events',
    icon: Video,
    color: 'pink',
    groups: [
      {
        name: 'Speed Sign Detection',
        description: 'Configure speed sign recognition events',
        settings: [
          { path: 'speedSign.enable', type: 'bool', label: 'Enable Detection', default: false },
          { path: 'speedSign.minSpeed', type: 'float', label: 'Min Speed', unit: 'km/h', default: 0, min: 0, max: 200, step: 0.1 },
          { path: 'speedSign.debounce', type: 'int', label: 'Debounce Time', unit: 'sec', default: 0, min: 0, max: 60 },
        ]
      }
    ],
    settings: [
      { path: 'speedSign.enable', type: 'bool', label: 'Enable speed sign events', default: false },
      { path: 'speedSign.minSpeed', type: 'float', label: 'Minimum speed for detection', default: 0 },
      { path: 'speedSign.debounce', type: 'int', label: 'Debounce time (seconds)', default: 0 },
    ]
  },
  notifier: {
    id: 'notifier',
    name: 'Notifier',
    icon: Volume2,
    color: 'cyan',
    groups: [
      {
        name: 'Audio Settings',
        description: 'Configure alert sound settings',
        settings: [
          { path: 'volume', type: 'str', label: 'Volume Level', default: '50', options: ['0', '25', '50', '75', '100'] },
        ]
      }
    ],
    settings: [
      { path: 'volume', type: 'str', label: 'Alert sound volume', default: '50' },
    ]
  },
  'lta-device-monitor': {
    id: 'lta-device-monitor',
    name: 'LTA Device Monitor',
    icon: Activity,
    color: 'indigo',
    groups: [
      {
        name: 'Panic Button',
        description: 'Configure panic button behavior',
        settings: [
          { path: 'panicEvent.debounceSec', type: 'int', label: 'Debounce Time', unit: 'sec', default: 0, min: 0, max: 60 },
        ]
      }
    ],
    settings: [
      { path: 'panicEvent.debounceSec', type: 'int', label: 'Panic button debounce (seconds)', default: 0 },
    ]
  },
  sysmon: {
    id: 'sysmon',
    name: 'Sysmon',
    icon: Monitor,
    color: 'slate',
    groups: [
      {
        name: 'Application Management',
        description: 'Manage which applications are disabled on the device',
        settings: [
          { path: 'disabledApps', type: 'array', label: 'Disabled Apps', default: [] },
        ]
      }
    ],
    settings: [
      { path: 'disabledApps', type: 'array', label: 'Disabled Apps (applications to disable)', default: [] },
    ]
  }
};

// Array Input Component for disabledApps and similar fields
function ArrayInput({ value, onChange }) {
  const [inputValue, setInputValue] = useState('');

  const addItem = () => {
    if (inputValue.trim() && !value.includes(inputValue.trim())) {
      onChange([...value, inputValue.trim()]);
      setInputValue('');
    }
  };

  const removeItem = (item) => {
    onChange(value.filter(v => v !== item));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <div className="space-y-2 w-64">
      <div className="flex gap-2">
        <Input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g., lta-wifi"
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          disabled={!inputValue.trim()}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((item, idx) => (
            <Badge key={idx} variant="secondary" className="flex items-center gap-1 pr-1">
              {item}
              <button
                type="button"
                onClick={() => removeItem(item)}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {value.length === 0 && (
        <p className="text-xs text-muted-foreground">Empty array (will send [])</p>
      )}
    </div>
  );
}

// Helper to set nested value in object
const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]]) {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
  return obj;
};

function ConfigBindingPanel({ onConfigGenerated }) {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedSettings, setSelectedSettings] = useState({});
  const [settingValues, setSettingValues] = useState({});

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Toggle setting selection
  const toggleSetting = (categoryId, settingPath) => {
    const key = `${categoryId}.${settingPath}`;
    const isCurrentlySelected = selectedSettings[key];
    
    if (isCurrentlySelected) {
      // Deselect - remove from both states
      setSelectedSettings(prev => {
        const newSelected = { ...prev };
        delete newSelected[key];
        return newSelected;
      });
      setSettingValues(prev => {
        const newValues = { ...prev };
        delete newValues[key];
        return newValues;
      });
    } else {
      // Select - add to both states
      const category = CONFIG_CATEGORIES[categoryId];
      const setting = category.settings.find(s => s.path === settingPath);
      
      setSelectedSettings(prev => ({
        ...prev,
        [key]: true
      }));
      
      if (setting) {
        setSettingValues(prev => ({
          ...prev,
          [key]: setting.default
        }));
      }
    }
  };

  // Update setting value
  const updateSettingValue = (categoryId, settingPath, value) => {
    const key = `${categoryId}.${settingPath}`;
    setSettingValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Build configuration object from selected settings
  const buildConfig = () => {
    const config = {};
    
    Object.entries(selectedSettings).forEach(([key, isSelected]) => {
      if (!isSelected) return;
      
      const [categoryId, ...pathParts] = key.split('.');
      const settingPath = pathParts.join('.');
      const value = settingValues[key];
      
      if (!config[categoryId]) {
        config[categoryId] = {};
      }
      
      setNestedValue(config[categoryId], settingPath, value);
    });
    
    return config;
  };

  // Update parent component when config changes
  useEffect(() => {
    const config = buildConfig();
    if (onConfigGenerated) {
      onConfigGenerated(config);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSettings, settingValues]);

  // Render setting input based on type
  const renderSettingInput = (categoryId, setting) => {
    const key = `${categoryId}.${setting.path}`;
    const value = settingValues[key];
    const isSelected = selectedSettings[key];

    if (!isSelected) return null;

    switch (setting.type) {
      case 'bool':
        return (
          <button
            onClick={() => updateSettingValue(categoryId, setting.path, !value)}
            className={`p-2 rounded ${value ? 'bg-green-500 text-white' : 'bg-gray-300'}`}
          >
            {value ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
          </button>
        );
      
      case 'int':
        return (
          <Input
            type="number"
            value={value ?? ''}
            onChange={(e) => updateSettingValue(categoryId, setting.path, parseInt(e.target.value) || 0)}
            className="w-32"
          />
        );
      
      case 'float':
        return (
          <Input
            type="number"
            step="0.1"
            value={value ?? ''}
            onChange={(e) => updateSettingValue(categoryId, setting.path, parseFloat(e.target.value) || 0)}
            className="w-32"
          />
        );
      
      case 'str':
        if (setting.options) {
          return (
            <select
              value={value ?? ''}
              onChange={(e) => updateSettingValue(categoryId, setting.path, e.target.value)}
              className="p-2 border rounded w-48"
            >
              {setting.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          );
        }
        return (
          <Input
            type="text"
            value={value ?? ''}
            onChange={(e) => updateSettingValue(categoryId, setting.path, e.target.value)}
            className="w-32"
          />
        );
      
      case 'array':
        return (
          <ArrayInput
            value={value || []}
            onChange={(newValue) => updateSettingValue(categoryId, setting.path, newValue)}
          />
        );
      
      default:
        return null;
    }
  };

  // Get color classes for category
  const getColorClasses = (color) => {
    const colors = {
      red: 'bg-red-100 text-red-700 border-red-200',
      orange: 'bg-orange-100 text-orange-700 border-orange-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      green: 'bg-green-100 text-green-700 border-green-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
      pink: 'bg-pink-100 text-pink-700 border-pink-200',
      cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      slate: 'bg-slate-100 text-slate-700 border-slate-200',
    };
    return colors[color] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Count selected settings in category
  const getSelectedCount = (categoryId) => {
    return Object.keys(selectedSettings).filter(key => 
      key.startsWith(`${categoryId}.`) && selectedSettings[key]
    ).length;
  };

  // Get total selected count
  const getTotalSelectedCount = () => {
    return Object.values(selectedSettings).filter(Boolean).length;
  };

  return (
    <div className="space-y-4">
      {/* Header with selected count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-blue-500" />
          <span className="text-sm text-muted-foreground">
            Select the configurations you want to send to the device twin
          </span>
        </div>
        <Badge variant="outline" className="text-sm">
          {getTotalSelectedCount()} configurations selected
        </Badge>
      </div>

      {/* Categories */}
      <div className="space-y-2">
        {Object.values(CONFIG_CATEGORIES).map((category) => {
          const Icon = category.icon;
          const isExpanded = expandedCategories[category.id];
          const selectedCount = getSelectedCount(category.id);
          
          return (
            <Card key={category.id} className={`border ${selectedCount > 0 ? 'border-green-300 bg-green-50/30' : ''}`}>
              <CardHeader 
                className="py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <div className={`p-2 rounded ${getColorClasses(category.color)}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <CardTitle className="text-sm font-medium">{category.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedCount > 0 && (
                      <Badge variant="default" className="bg-green-600">
                        {selectedCount} selected
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {category.settings.length} options
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-0 pb-4">
                  {/* Render with groups if available */}
                  {category.groups ? (
                    <div className="space-y-4 ml-4">
                      {category.groups.map((group, groupIdx) => (
                        <div key={groupIdx} className="border rounded-lg overflow-hidden">
                          <div className="bg-gray-100 px-4 py-2 border-b">
                            <h4 className="font-medium text-sm">{group.name}</h4>
                            {group.description && (
                              <p className="text-xs text-muted-foreground">{group.description}</p>
                            )}
                          </div>
                          <div className="p-3 space-y-3">
                            {group.settings.map((setting) => {
                              const key = `${category.id}.${setting.path}`;
                              const isSelected = selectedSettings[key];
                              const value = settingValues[key];
                              
                              return (
                                <div 
                                  key={setting.path}
                                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                    isSelected 
                                      ? 'bg-green-50 border-green-300 shadow-sm' 
                                      : 'bg-white border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <button
                                      onClick={() => toggleSetting(category.id, setting.path)}
                                      className={`p-1.5 rounded-full transition-colors ${
                                        isSelected 
                                          ? 'bg-green-500 text-white' 
                                          : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                                      }`}
                                    >
                                      {isSelected ? (
                                        <Check className="w-3 h-3" />
                                      ) : (
                                        <Plus className="w-3 h-3" />
                                      )}
                                    </button>
                                    <div>
                                      <p className="text-sm font-medium">{setting.label}</p>
                                      {setting.unit && (
                                        <p className="text-xs text-muted-foreground">Unit: {setting.unit}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    {isSelected && setting.type === 'bool' && (
                                      <button
                                        onClick={() => updateSettingValue(category.id, setting.path, !value)}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                          value 
                                            ? 'bg-green-500 text-white' 
                                            : 'bg-gray-300 text-gray-600'
                                        }`}
                                      >
                                        {value ? 'ON' : 'OFF'}
                                      </button>
                                    )}
                                    {isSelected && setting.type === 'int' && (
                                      <div className="flex items-center gap-2">
                                        <Input
                                          type="number"
                                          min={setting.min}
                                          max={setting.max}
                                          value={value ?? ''}
                                          onChange={(e) => updateSettingValue(category.id, setting.path, parseInt(e.target.value) || 0)}
                                          className="w-24 text-center"
                                        />
                                        {setting.unit && (
                                          <span className="text-xs text-muted-foreground">{setting.unit}</span>
                                        )}
                                      </div>
                                    )}
                                    {isSelected && setting.type === 'float' && (
                                      <div className="flex items-center gap-2">
                                        <Input
                                          type="number"
                                          step={setting.step || 0.1}
                                          min={setting.min}
                                          max={setting.max}
                                          value={value ?? ''}
                                          onChange={(e) => updateSettingValue(category.id, setting.path, parseFloat(e.target.value) || 0)}
                                          className="w-24 text-center"
                                        />
                                      </div>
                                    )}
                                    {isSelected && setting.type === 'str' && setting.options && (
                                      <select
                                        value={value ?? ''}
                                        onChange={(e) => updateSettingValue(category.id, setting.path, e.target.value)}
                                        className="p-2 border rounded text-sm"
                                      >
                                        {setting.options.map(opt => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    )}
                                    {isSelected && setting.type === 'str' && !setting.options && (
                                      <Input
                                        type="text"
                                        value={value ?? ''}
                                        onChange={(e) => updateSettingValue(category.id, setting.path, e.target.value)}
                                        className="w-32"
                                      />
                                    )}
                                    {isSelected && setting.type === 'array' && (
                                      <div className="space-y-2">
                                        <ArrayInput
                                          value={value || []}
                                          onChange={(newValue) => updateSettingValue(category.id, setting.path, newValue)}
                                        />
                                        {category.id === 'sysmon' && setting.path === 'disabledApps' && (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => updateSettingValue(category.id, setting.path, [...SYSMON_ALL_APPS])}
                                          >
                                            Disable all apps
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                    {!isSelected && (
                                      <span className="text-xs text-muted-foreground">
                                        Default: {Array.isArray(setting.default) ? '[]' : String(setting.default)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Original rendering for categories without groups */
                    <div className="space-y-2 ml-8">
                      {category.settings.map((setting) => {
                        const key = `${category.id}.${setting.path}`;
                        const isSelected = selectedSettings[key];
                        
                        return (
                          <div 
                            key={setting.path}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isSelected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => toggleSetting(category.id, setting.path)}
                                className={`p-1 rounded ${isSelected ? 'text-green-600' : 'text-gray-400'}`}
                              >
                                {isSelected ? (
                                  <Trash2 className="w-4 h-4" />
                                ) : (
                                  <Plus className="w-4 h-4" />
                                )}
                              </button>
                              <div>
                                <p className="text-sm font-medium">{setting.label}</p>
                                <p className="text-xs text-muted-foreground font-mono">
                                  {category.id}.{setting.path}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {setting.type}
                              </Badge>
                              {renderSettingInput(category.id, setting)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Preview */}
      {getTotalSelectedCount() > 0 && (
        <Card className="bg-gray-50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Configuration Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-white p-3 rounded border text-xs overflow-x-auto max-h-48">
              {JSON.stringify(buildConfig(), null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ConfigBindingPanel;
